import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../../services/api';
import {
  CarteLigne, Feuille, Fiche, Kv, LienOrdinateur, Liste, Pill, Plaque, Puces, Recherche, Statut,
  dateFr, messageErreur, normaliser, useFicheOuverte, useToast,
} from './MobileUI';
import './VipMobile.css';

// ═══════════════════════════════════════════════════════════════════════════
// Réception VIP — version téléphone (2026-10-24).
//
// Le cas d'usage : sur le parking, devant la voiture, l'hôte d'accueil ou le
// chef d'atelier fait avancer le statut (« Prêt », « Livré ») et saisit le
// n° d'OR. La file fait ressortir les dossiers OUBLIÉS : ancienneté du dépôt
// en jours (rouge au-delà de 30 j), le plus ancien en haut.
//
// Mêmes routes et mêmes règles que ReceptionVip.js :
//   - liste : `GET /reception-vip` (non livrés), puis recherche dans le
//     navigateur sur client, société, plaque et n° d'OR (la route ne cherche
//     que `client_username`) ;
//   - « À traiter » = non livré et prochaine action pas dans le futur, comme
//     l'onglet ordinateur (ici en date LOCALE : l'ordinateur prend la date
//     UTC, qui passe au lendemain dès 20 h aux Antilles) ;
//   - statut : `PUT /:id {statut}` seul. « Livré » archive le dossier : il
//     demande une confirmation dans la feuille ;
//   - n° d'OR : `PATCH /:id/num-or`, par un bouton « Enregistrer » (pas à la
//     perte du focus, trop d'enregistrements involontaires au doigt) ;
//   - restitution et prochaine action : supprimées le 2026-10-26 (champs et données) ;
//   - photos : `GET /:id/photos` (base64, ~600 Ko) chargées à l'ouverture du
//     bloc seulement ; position : lien « Ouvrir dans Maps » au lieu de l'iframe.
// Rafraîchissement toutes les 30 s comme l'ordinateur, suspendu quand l'onglet
// est caché. Reste sur ordinateur : clients VIP, création, facture, OR en
// cours, planification, suppression, archives, nom du client.
// ═══════════════════════════════════════════════════════════════════════════

// Référentiel STATUTS de ReceptionVip.js (mêmes valeurs, libellés, teintes) ;
// light-dark() garde la teinte exacte en clair et l'éclaircit en sombre.
const STATUTS = [
  { valeur: 'recu', label: 'Reçu', couleur: 'light-dark(#3b82f6, color-mix(in srgb, #3b82f6 60%, white))' },
  { valeur: 'en_diagnostic', label: 'En diagnostic', couleur: 'light-dark(#f59e0b, color-mix(in srgb, #f59e0b 60%, white))' },
  { valeur: 'pieces_commande', label: 'Pièces en commande', court: 'Pièces', couleur: 'light-dark(#a855f7, color-mix(in srgb, #a855f7 60%, white))' },
  { valeur: 'attente_garantie', label: 'Attente retour garantie', court: 'Garantie', couleur: 'light-dark(#06b6d4, color-mix(in srgb, #06b6d4 60%, white))' },
  { valeur: 'attente_accord', label: 'Attente accord client', court: 'Accord client', couleur: 'light-dark(#ec4899, color-mix(in srgb, #ec4899 60%, white))' },
  { valeur: 'pret', label: 'Prêt', couleur: 'light-dark(#10b981, color-mix(in srgb, #10b981 60%, white))' },
  { valeur: 'livre', label: 'Livré', couleur: 'light-dark(#6b7280, color-mix(in srgb, #6b7280 60%, white))' },
];
const statutDe = (v) => STATUTS.find((s) => s.valeur === v) || { valeur: v, label: v || '—', couleur: 'var(--text-muted)' };
const SEUIL_OUBLI = 30;

const jourDe = (v) => (v ? String(v).split('T')[0] : '');
const anciennete = (d) => Math.max(0, Math.floor((Date.now() - new Date(d.created_at).getTime()) / 86400000));
const nomClient = (d) => d.client_societe || [d.client_nom, d.client_prenom].filter(Boolean).join(' ') || d.client_username || 'Client inconnu';
const immatNorm = (s) => String(s || '').toUpperCase().replace(/[\s\-_]/g, '');

export default function VipMobile({ user, onVersionOrdinateur }) {
  const estAdmin = user?.role === 'admin' || user?.role === 'super-admin';
  const [dossiers, setDossiers] = useState([]);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState('');
  const [file, setFile] = useState('atraiter');
  const [recherche, setRecherche] = useState('');
  const [toast, afficher] = useToast();

  const charger = useCallback(async (silencieux) => {
    try {
      const r = await api.get('/reception-vip');
      setDossiers(r.data.dossiers || []);
      setErreur('');
    } catch (e) {
      if (!silencieux) setErreur(messageErreur(e, 'Les dossiers n’ont pas pu être chargés.'));
    } finally {
      setChargement(false);
    }
  }, []);
  useEffect(() => { charger(false); }, [charger]);

  // 30 s comme l'ordinateur, mais pas quand l'onglet est caché (batterie, 4G) ;
  // rechargement immédiat au retour.
  useEffect(() => {
    const tic = () => { if (document.visibilityState === 'visible') charger(true); };
    document.addEventListener('visibilitychange', tic);
    const minuteur = setInterval(tic, 30000);
    return () => { document.removeEventListener('visibilitychange', tic); clearInterval(minuteur); };
  }, [charger]);

  // « Prochaine action » supprimée le 2026-10-26 : À traiter = tous les dossiers en cours.
  const comptes = useMemo(() => {
    const c = { atraiter: dossiers.length, tous: dossiers.length };
    STATUTS.forEach((s) => { c[s.valeur] = 0; });
    dossiers.forEach((d) => {
      if (c[d.statut] != null) c[d.statut] += 1;
    });
    return c;
  }, [dossiers]);

  // Recherche : client, société, nom du compte VIP, plaque (sans tirets), n° d'OR.
  const q = normaliser(recherche);
  const qImmat = immatNorm(recherche);
  const correspond = useCallback((d) => !q
    || [d.client_username, d.client_societe, d.client_nom, d.client_prenom, d.num_or].some((v) => normaliser(v).includes(q))
    || (qImmat.length >= 2 && immatNorm(d.plaque).includes(qImmat)), [q, qImmat]);

  // Le plus ancien dépôt en haut : ce qui attend depuis longtemps est ce qui presse.
  const visibles = useMemo(() => dossiers
    .filter((d) => (q ? correspond(d)
      : file === 'atraiter' || file === 'tous' ? true : d.statut === file))
    .sort((a, b) => new Date(a.created_at) - new Date(b.created_at)), [dossiers, file, q, correspond]);

  const cle = useCallback((d) => d.id, []);
  const fiche = useFicheOuverte(visibles, cle);

  const majLocale = useCallback((id, champs) => setDossiers((prev) => prev.map((d) => (d.id === id ? { ...d, ...champs } : d))), []);
  const retirer = (id) => {
    if (fiche.suivant) fiche.suivant(); else if (fiche.precedent) fiche.precedent(); else fiche.fermer();
    setDossiers((prev) => prev.filter((d) => d.id !== id));
  };

  // Puces de statut : seulement celles qui ont des dossiers (et celle choisie).
  const puces = [
    { cle: 'atraiter', libelle: 'À traiter', compte: comptes.atraiter },
    ...STATUTS.filter((s) => s.valeur !== 'livre' && (comptes[s.valeur] > 0 || file === s.valeur))
      .map((s) => ({ cle: s.valeur, libelle: s.court || s.label, compte: comptes[s.valeur] })),
    { cle: 'tous', libelle: 'Tous', compte: comptes.tous },
  ];

  return (
    <div className="mb-page">
      <div className="mb-outils">
        <Recherche id="mb-vip-q" valeur={recherche} onChange={setRecherche} placeholder="Plaque, client, n° d’OR" />
      </div>
      {!q && <Puces label="Statut" valeur={file} onChange={setFile} options={puces} />}

      <Liste
        elements={visibles}
        cle={cle}
        chargement={chargement}
        erreur={erreur}
        vide={q ? 'Aucun dossier en cours ne correspond.' : file === 'atraiter' ? 'Aucun dossier à traiter aujourd’hui.' : 'Aucun dossier dans cette file.'}
        groupe={(d) => (anciennete(d) > SEUIL_OUBLI ? `Déposés il y a plus de ${SEUIL_OUBLI} jours` : `Moins de ${SEUIL_OUBLI} jours`)}
        onOuvrir={fiche.ouvrir}
        carte={(d) => {
          const s = statutDe(d.statut);
          const j = anciennete(d);
          return {
            couleur: s.couleur,
            contenu: (
              <>
                <CarteLigne avant={<Plaque immat={d.plaque} />} titre={nomClient(d)} age={`${j} j`} retard={j > SEUIL_OUBLI} />
                {(d.marque || d.description) && (
                  <span className="mb-carte-texte">{[d.marque, d.description].filter(Boolean).join(' · ')}</span>
                )}
                <span className="mb-carte-meta">
                  <Statut libelle={s.label} couleur={s.couleur} />
                  {d.num_or ? <Pill>OR {d.num_or}</Pill> : <Pill ton="attente">OR à saisir</Pill>}
                  {d.in_parking && <Pill>🅿️ Parking</Pill>}
                  {estAdmin && d.concession_nom && <Pill>{d.concession_nom}</Pill>}
                </span>
              </>
            ),
          };
        }}
      />
      <LienOrdinateur onClick={onVersionOrdinateur}>Clients VIP, création, archives : version ordinateur</LienOrdinateur>

      {fiche.ouverte && (
        <FicheVip
          key={fiche.ouverte.id}
          dossier={fiche.ouverte}
          position={fiche.index}
          total={visibles.length}
          onRetour={fiche.fermer}
          onPrecedent={fiche.precedent}
          onSuivant={fiche.suivant}
          onMaj={majLocale}
          onRetire={retirer}
          afficher={afficher}
          onVersionOrdinateur={onVersionOrdinateur}
        />
      )}
      {toast}
    </div>
  );
}

function FicheVip({ dossier, position, total, onRetour, onPrecedent, onSuivant, onMaj, onRetire, afficher, onVersionOrdinateur }) {
  const [numOr, setNumOr] = useState(dossier.num_or || '');
  const [occupe, setOccupe] = useState('');
  const [erreur, setErreur] = useState('');
  const [feuille, setFeuille] = useState(false);
  const [photos, setPhotos] = useState(null); // null = pas encore demandées
  const [photosEtat, setPhotosEtat] = useState('');
  const [agrandie, setAgrandie] = useState(null);

  const s = statutDe(dossier.statut);
  const j = anciennete(dossier);
  const depot = new Date(dossier.created_at);
  const aGeo = dossier.latitude != null && dossier.longitude != null && dossier.latitude !== '' && dossier.longitude !== '';

  const enregistrerOr = async () => {
    const v = numOr.trim();
    setOccupe('or'); setErreur('');
    try {
      await api.patch(`/reception-vip/${dossier.id}/num-or`, { num_or: v || null });
      onMaj(dossier.id, { num_or: v || null });
      setNumOr(v);
      afficher(v ? `N° d’OR ${v} enregistré` : 'N° d’OR retiré');
    } catch (e) {
      setErreur(messageErreur(e, 'Le n° d’OR n’a pas pu être enregistré.'));
    } finally {
      setOccupe('');
    }
  };

  const chargerPhotos = () => {
    if (photos !== null || photosEtat === 'chargement') return;
    setPhotosEtat('chargement');
    api.get(`/reception-vip/${dossier.id}/photos`)
      .then((r) => {
        const liste = [];
        if (r.data.photo_plaque) liste.push({ src: r.data.photo_plaque, libelle: 'Plaque' });
        (r.data.photos_vehicule || []).forEach((p, i) => liste.push({ src: p, libelle: `Véhicule ${i + 1}` }));
        setPhotos(liste); setPhotosEtat('');
      })
      .catch((e) => setPhotosEtat(messageErreur(e, 'Photos indisponibles.')));
  };

  return (
    <Fiche
      titre={`Dossier #${dossier.id}`}
      position={position}
      total={total}
      onRetour={onRetour}
      onPrecedent={onPrecedent}
      onSuivant={onSuivant}
      pied={<button type="button" className="mb-bouton mb-bouton--principal" onClick={() => setFeuille(true)}>Changer le statut</button>}
    >
      <div className="mb-entete">
        <div className="mb-entete-haut"><Plaque immat={dossier.plaque} />{dossier.marque || ''}</div>
        <div>
          <div className="mb-entete-titre">{nomClient(dossier)}</div>
          <div className="mb-entete-sous">
            {[`Déposé le ${depot.toLocaleDateString('fr-FR')} à ${depot.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}`,
              `il y a ${j} jour${j > 1 ? 's' : ''}`, dossier.concession_nom].filter(Boolean).join(' · ')}
          </div>
        </div>
      </div>

      <button type="button" className="mb-statut-bouton" style={{ '--mb-c': s.couleur }} onClick={() => setFeuille(true)}>
        <span className="mb-statut-pastille" />
        <b style={{ color: s.couleur }}>{s.label}</b>
        <small>{j > SEUIL_OUBLI ? `Déposé depuis ${j} jours` : 'Statut du dossier'}</small>
        <span className="mb-statut-changer">Changer</span>
      </button>

      <div className="mb-carton">
        <div className="mb-champ">
          <label className="mb-etiquette" htmlFor={`mb-vip-or-${dossier.id}`}>N° d’OR</label>
          <div className="mb-saisie mb-vip-saisie">
            <input id={`mb-vip-or-${dossier.id}`} inputMode="numeric" value={numOr} placeholder="à saisir"
              onChange={(e) => setNumOr(e.target.value)} enterKeyHint="done" />
            <button type="button" disabled={!!occupe || numOr.trim() === (dossier.num_or || '')} onClick={enregistrerOr}>
              {occupe === 'or' ? '…' : 'Enregistrer'}
            </button>
          </div>
        </div>
        {erreur && <p className="mb-aide mb-aide--erreur">{erreur}</p>}
      </div>

      {dossier.description && (
        <div className="mb-citation">{dossier.description}<small>Description saisie à la dépose</small></div>
      )}

      {/* Bloc maison (même dessin que Bloc du socle) : il faut savoir QUAND il
          s'ouvre pour ne charger les photos qu'à ce moment-là. */}
      <details className="mb-bloc" onToggle={(e) => { if (e.currentTarget.open) chargerPhotos(); }}>
        <summary>
          <span>Photos de dépose</span>
          {photos && <span className="mb-bloc-compte">{photos.length}</span>}
          <span className="mb-bloc-resume">{photos ? '' : 'chargées à l’ouverture'}</span>
        </summary>
        <div className="mb-bloc-corps">
          {photosEtat === 'chargement' && <p className="mb-aide">Chargement des photos…</p>}
          {photosEtat && photosEtat !== 'chargement' && <p className="mb-aide mb-aide--erreur">{photosEtat}</p>}
          {photos && photos.length === 0 && <p className="mb-aide">Aucune photo pour ce dossier.</p>}
          {photos && photos.length > 0 && (
            <div className="mb-vip-photos">
              {photos.map((p) => (
                <button key={p.libelle} type="button" onClick={() => setAgrandie(p)} aria-label={`Agrandir : ${p.libelle}`}>
                  <img src={`data:image/jpeg;base64,${p.src}`} alt="" />
                  <small>{p.libelle}</small>
                </button>
              ))}
            </div>
          )}
        </div>
      </details>

      {aGeo && (
        <a className="mb-carton mb-vip-maps" href={`https://maps.google.com/?q=${dossier.latitude},${dossier.longitude}`}
          target="_blank" rel="noopener noreferrer">
          <span>📍 Position de dépose</span><b>Ouvrir dans Maps</b>
        </a>
      )}

      <Kv lignes={[
        ['Identifiant VIP', dossier.client_username],
        dossier.facture_date && ['Facturé le', dateFr(jourDe(dossier.facture_date))],
      ]} />

      <p className="mb-aide mb-aide--centre">Nom du client, facture, OR en cours, planification, suppression : version ordinateur.</p>
      <LienOrdinateur onClick={onVersionOrdinateur} />

      {feuille && (
        <FeuilleStatut dossier={dossier} onFermer={() => setFeuille(false)}
          onFait={(valeur) => {
            setFeuille(false);
            if (valeur === 'livre') { afficher('Dossier livré et archivé'); onRetire(dossier.id); return; }
            onMaj(dossier.id, { statut: valeur });
            afficher(`Statut : ${statutDe(valeur).label}`);
          }} />
      )}

      {agrandie && (
        <div className="mb-vip-plein" role="dialog" aria-modal="true" aria-label={`Photo : ${agrandie.libelle}`}>
          <div className="mb-vip-plein-barre">
            <span>{agrandie.libelle}</span>
            <button type="button" onClick={() => setAgrandie(null)}>Fermer</button>
          </div>
          <img src={`data:image/jpeg;base64,${agrandie.src}`} alt={agrandie.libelle} />
        </div>
      )}
    </Fiche>
  );
}

function FeuilleStatut({ dossier, onFermer, onFait }) {
  const [confirmerLivre, setConfirmerLivre] = useState(false);
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState('');

  const appliquer = async (valeur) => {
    if (envoi) return;
    setEnvoi(true); setErreur('');
    try {
      await api.put(`/reception-vip/${dossier.id}`, { statut: valeur });
      onFait(valeur);
    } catch (e) {
      setErreur(messageErreur(e, 'Le statut n’a pas pu être changé.'));
      setEnvoi(false);
    }
  };
  const choisir = (valeur) => {
    if (valeur === dossier.statut) { onFermer(); return; }
    if (valeur === 'livre') { setConfirmerLivre(true); return; }
    setConfirmerLivre(false);
    appliquer(valeur);
  };

  return (
    <Feuille titre="Statut du dossier" onFermer={onFermer}>
      <div className="mb-options">
        {STATUTS.map((o) => (
          <button key={o.valeur} type="button" disabled={envoi}
            className={`mb-option${o.valeur === dossier.statut || (confirmerLivre && o.valeur === 'livre') ? ' is-actif' : ''}`}
            style={{ '--mb-c': o.couleur }} onClick={() => choisir(o.valeur)}>
            <span className="mb-option-pastille" />
            {o.label}
            {o.valeur === 'livre' && <small>archive le dossier</small>}
          </button>
        ))}
      </div>
      {confirmerLivre && (
        <div className="mb-carton mb-vip-confirmer">
          <p className="mb-aide">Le véhicule a bien été rendu au client ? Le dossier part dans les archives et sort de cette liste.</p>
          <div className="mb-feuille-pied">
            <button type="button" className="mb-bouton" onClick={() => setConfirmerLivre(false)} disabled={envoi}>Annuler</button>
            <button type="button" className="mb-bouton mb-bouton--principal" onClick={() => appliquer('livre')} disabled={envoi}>
              {envoi ? 'Envoi…' : 'Confirmer la livraison'}
            </button>
          </div>
        </div>
      )}
      {erreur && <p className="mb-aide mb-aide--erreur">{erreur}</p>}
    </Feuille>
  );
}
